import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { UserContext } from '../../../context/UserContext';
import { useContext } from 'react';
import { chatsApi, ChatType } from '../../../api/chats';
import { battlesApi } from '../../../api';
import websocketApi from '../../../api/websocket';
import { getAccessToken } from '../../../api/token';
import { filesApi } from '../../../api/files';
import HrnbCard from '../../../components/controls/HrnbCard';
import HrnbAlert from '../../../components/controls/HrnbAlert';
import HrnbLoader from '../../../components/controls/HrnbLoader';
import HrnbButton from '../../../components/controls/HrnbButton';
import HrnbInput from '../../../components/controls/HrnbInput';
import ChatMessage from '../../../components/chats/ChatMessage';
import BattleChatAddParticipantModal from '../../../components/battles/BattleChatAddParticipantModal';
import '../../../css/pages/BattleDetailPage.css';
import { PlusIcon, UploadIcon, SendIcon } from '../../../icons';

/**
 * Форматирование разделителя даты для ленты сообщений.
 * Возвращает "Сегодня", "Вчера" или дату в формате "12 мая"
 * (без года, если текущий год; с годом — если другой).
 */
function formatDateSeparator(date) {
  const now = new Date();
  const msgDate = new Date(date);
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const msgDay = new Date(msgDate.getFullYear(), msgDate.getMonth(), msgDate.getDate());
  const diffDays = Math.round((today - msgDay) / 86400000);

  if (diffDays === 0) return 'Сегодня';
  if (diffDays === 1) return 'Вчера';

  const months = ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня',
    'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря'];
  const sameYear = msgDate.getFullYear() === now.getFullYear();
  return sameYear
    ? `${msgDate.getDate()} ${months[msgDate.getMonth()]}`
    : `${msgDate.getDate()} ${months[msgDate.getMonth()]} ${msgDate.getFullYear()}`;
}

/** Ключ дня для группировки сообщений по дате */
function getDayKey(date) {
  const d = new Date(date);
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

/**
 * Вкладка чата баттла (Групповой чат участников)
 *
 * Использует стандартное API чатов для группового чата с типом BATTLE_PARTICIPANTS
 *
 * Структура чата:
 * - chatType: "BATTLE_PARTICIPANTS"
 * - title: "Участники: {Название баттла}"
 * - adminIds: [UUID организатора]
 * - participantIds: [UUID участников]
 *
 * WebSocket интеграция для real-time сообщений
 */
const BattleChatTab = ({ battle, chatId, isUserOrganizer = false }) => {
  const { id: battleId } = useParams();
  const { currentUser } = useContext(UserContext);
  
  const [chat, setChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [messageText, setMessageText] = useState('');
  const [sending, setSending] = useState(false);
  const [fetchingMessages, setFetchingMessages] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);
  const [lastMessageId, setLastMessageId] = useState(null); // Для защиты от дубликатов
  const [attachments, setAttachments] = useState([]); // Вложения к сообщению
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);

  const fileInputRef = useRef(null);

  // Управление добавлением участников
  const [showAddParticipantModal, setShowAddParticipantModal] = useState(false);
  const [isChatAdmin, setIsChatAdmin] = useState(false);

  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);
  const wsSubscriptionRef = useRef(null);

  // Определение ID чата: из пропсов, из объекта баттла, или через API
  const effectiveChatId = chatId || battle?.participantsChatId || battle?.chatId;

  // Инициализация WebSocket
  useEffect(() => {
    if (!currentUser) return;

    const token = getAccessToken();

    // Инициализируем WebSocket если ещё не инициализирован
    websocketApi.init(
      (event) => {
        if (event.connected) {
          setWsConnected(true);
        }
        if (event.error) {
          setWsConnected(false);
        }
      },
      null,
      token
    );

    return () => {
      // Отписка при размонтировании
      if (wsSubscriptionRef.current) {
        wsSubscriptionRef.current.unsubscribe();
        wsSubscriptionRef.current = null;
      }
    };
  }, [currentUser]);

  // Подписка на WebSocket чат при загрузке чата
  useEffect(() => {
    if (!wsConnected || !chat?.id) {
      return;
    }

    // Отписка от предыдущего чата
    if (wsSubscriptionRef.current) {
      wsSubscriptionRef.current.unsubscribe();
      wsSubscriptionRef.current = null;
    }

    // Подписка на новый чат
    const handleNewMessage = (messageData) => {
      // Добавляем данные о текущем пользователе, если это наше сообщение
      // WebSocket эхо может не содержать полные данные sender
      const enrichedMessageData = {
        ...messageData,
        sender: messageData.sender || {
          id: currentUser?.id,
          userId: currentUser?.id,
          displayName: currentUser?.profiles?.[0]?.displayName || currentUser?.displayName || currentUser?.username || 'Я'
        }
      };

      setMessages(prev => {
        // Проверяем, нет ли уже такого сообщения (защита от дубликатов по ID)
        const exists = prev.some(msg => msg.id === enrichedMessageData.id);
        if (exists) {
          return prev;
        }

        // Дополнительная проверка: не это ли сообщение мы только что отправили
        if (lastMessageId === enrichedMessageData.id) {
          return prev;
        }

        return [...prev, enrichedMessageData];
      });

      // Оптимизированная прокрутка без лишнего таймаута
      requestAnimationFrame(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      });
    };

    // Подписываемся на стандартный топик чата
    wsSubscriptionRef.current = websocketApi.subscribeToChat(chat.id, handleNewMessage);

    if (wsSubscriptionRef.current) {
      websocketApi.joinChat(chat.id);
    }

    return () => {
      if (wsSubscriptionRef.current) {
        websocketApi.leaveChat(chat.id);
        wsSubscriptionRef.current.unsubscribe();
        wsSubscriptionRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wsConnected, chat?.id]);

  // Загрузка чата баттла через стандартное API чатов
  useEffect(() => {
    let mounted = true;

    const loadChat = async () => {
      if (!battleId) {
        setError('ID баттла не найден');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        let chatIdToUse = effectiveChatId;

        // Если chatId не передан, загружаем чат участников через API баттлов
        if (!chatIdToUse) {
          try {
            const chatData = await battlesApi.getParticipantsChat(battleId);
            chatIdToUse = chatData.id;
          } catch (err) {
            // Обработка 403 — у пользователя нет доступа к чату
            if (err.response?.status === 403) {
              if (mounted) {
                setError(
                  'У вас нет доступа к чату участников. ' +
                  'Возможно, вы ещё не одобрены организатором.'
                );
                setLoading(false);
              }
              return;
            }

            // Обработка 404 — чат ещё не создан
            if (err.response?.status === 404) {
              if (mounted) {
                setError(
                  'Чат ещё не создан. Обратитесь к организаторам.'
                );
                setLoading(false);
              }
              return;
            }

            // Другие ошибки
            if (mounted) {
              setError('Ошибка загрузки чата');
              setLoading(false);
            }
            return;
          }
        }

        // Загружаем чат через стандартное API
        const chatData = await chatsApi.getChat(chatIdToUse);

        if (mounted) {
          setChat(chatData);

          // Проверяем права на добавление участников
          const currentUserId = currentUser?.id || currentUser?.userId;
          const isAdminByChat = chatData.adminIds?.includes(currentUserId) || false;

          // Используем isUserOrganizer из пропсов (рассчитывается в BattleDetailPage)
          const hasAdminRights = isAdminByChat || isUserOrganizer;

          setIsChatAdmin(hasAdminRights);

          await loadMessages(chatData.id);
        }
      } catch (err) {
        if (mounted) {
          setError(err.response?.status === 404 
            ? 'Чат ещё не создан' 
            : 'Ошибка загрузки чата');
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };

    loadChat();

    return () => {
      mounted = false;
    };
  }, [battleId, effectiveChatId]);

  // Загрузка сообщений
  const loadMessages = async (chatId) => {
    try {
      setFetchingMessages(true);
      const data = await chatsApi.getMessages(chatId, { page: 0, size: 50, direction: 'ASC' });
      setMessages(data.content || data || []);
      
      // Прокрутка вниз после загрузки
      requestAnimationFrame(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      });
    } catch (err) {
      setError('Ошибка загрузки сообщений');
    } finally {
      setFetchingMessages(false);
    }
  };

  // Отправка сообщения через WebSocket с fallback на REST
  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    const hasText = messageText.trim().length > 0;
    const hasAttachments = attachments.length > 0;

    if (!hasText && !hasAttachments) return;

    try {
      setSending(true);

      // Фильтруем только валидные вложения с fileId
      const validAttachments = attachments.filter(att => att.id || att.fileId);

      // Проверка подключения WebSocket
      if (!websocketApi.isConnected()) {
        await sendViaRest(messageText.trim(), validAttachments);
        return;
      }

      // Формируем payload только с валидными данными
      const messagePayload = {
        content: messageText.trim()
      };

      // Добавляем attachmentIds (массив ID) - сервер ожидает именно так!
      if (validAttachments.length > 0) {
        messagePayload.attachmentIds = validAttachments.map(att => att.id || att.fileId);
      }

      // Отправляем объект (не строку!) - WebSocket сам сериализует
      const sent = websocketApi.sendMessage(chat.id, messagePayload);

      if (sent) {
        setMessageText('');
        setAttachments([]);
        // Сообщение появится через WebSocket (эхо от сервера)
      } else {
        await sendViaRest(messageText.trim(), validAttachments);
      }
    } catch (err) {
      // Обработка 429 (Rate Limit)
      if (err.response?.status === 429) {
        setError('Слишком много запросов. Подождите несколько секунд.');
        setTimeout(() => setError(null), 5000);
      } else {
        setError('Не удалось отправить сообщение');
      }
    } finally {
      setSending(false);
    }
  };

  // Отправка через REST API с обработкой 429 и поддержкой вложений
  const sendViaRest = async (text, attachmentsList = []) => {
    try {
      // Фильтруем только валидные вложения
      const validAttachments = attachmentsList.filter(att => att.id || att.fileId);

      // Формируем payload
      const payload = {
        content: text
      };

      // Добавляем attachmentIds (массив ID) - сервер ожидает именно так!
      if (validAttachments.length > 0) {
        payload.attachmentIds = validAttachments.map(att => att.id || att.fileId);
      }

      const response = await chatsApi.sendMessage(chat.id, payload);

      if (response) {
        // Сохраняем ID последнего отправленного сообщения для защиты от дубликатов
        setLastMessageId(response.id);

        // Добавляем сообщение в список сразу
        setMessages(prev => {
          const exists = prev.some(msg => msg.id === response.id);
          if (exists) return prev;
          return [...prev, response];
        });

        setMessageText('');
        setAttachments([]);
        requestAnimationFrame(() => {
          messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        });
      }
    } catch (err) {
      // Пробрасываем ошибку вверх для обработки 429
      if (err.response?.status === 429) {
        throw err;
      }
      throw new Error('Ошибка при отправке сообщения');
    }
  };

  // Прокрутка вниз
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Автоматическая прокрутка при новых сообщениях (только если пользователь внизу)
  useEffect(() => {
    // Проверяем, был ли пользователь near bottom перед обновлением
    const container = chatContainerRef.current;
    if (!container) return;

    const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 100;

    if (isNearBottom || messages.length <= 1) {
      requestAnimationFrame(() => {
        scrollToBottom();
      });
    }
  }, [messages]);

  // Загрузка файла
  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);

    // Проверка размера файла (макс. 30 MB)
    const MAX_FILE_SIZE = 30 * 1024 * 1024;
    if (file.size > MAX_FILE_SIZE) {
      setUploadError(`Файл слишком большой. Размер: ${fileSizeMB} MB. Максимальный размер: 30 MB`);
      e.target.value = '';
      return;
    }

    // Сбрасываем input чтобы можно было выбрать тот же файл повторно
    e.target.value = '';

    setUploading(true);
    setUploadError(null);

    try {
      let response;
      let fileType;

      const uploadOptions = chat?.id ? { entityType: 'CHAT', entityId: chat.id } : {};

      if (file.type.startsWith('image/')) {
        fileType = 'IMAGE';
        response = await filesApi.uploadImage(file, uploadOptions);
      } else if (file.type.startsWith('audio/')) {
        fileType = 'AUDIO';
        response = await filesApi.uploadAudio(file, uploadOptions);
      } else if (file.type.startsWith('video/')) {
        fileType = 'VIDEO';
        response = await filesApi.uploadVideo(file, uploadOptions);
      } else {
        fileType = 'DOCUMENT';
        response = await filesApi.upload(file, { type: fileType, ...uploadOptions });
      }

      const fileData = response?.file || response;
      const fileId = fileData?.id;

      if (!fileId) {
        throw new Error('Сервер не вернул ID файла');
      }

      setAttachments(prev => [...prev, {
        id: fileId,
        name: file.name,
        type: fileType,
        size: file.size,
        contentType: file.type
      }]);
    } catch (err) {
      console.error('[BattleChatTab] Failed to upload file:', err);

      let errorMessage = 'Не удалось загрузить файл';
      if (err.response?.status === 413) {
        errorMessage = `Файл слишком большой. Размер: ${fileSizeMB} MB`;
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.message) {
        errorMessage = err.message;
      }

      setUploadError(errorMessage);
    } finally {
      setUploading(false);
    }
  };

  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const getAttachmentIcon = (type) => {
    switch (type) {
      case 'IMAGE': return '🖼️';
      case 'AUDIO': return '🎵';
      case 'VIDEO': return '🎬';
      default: return '📎';
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  if (loading) {
    return (
      <div className="battle-chat-loading" style={{ padding: 40, textAlign: 'center' }}>
        <HrnbLoader size="lg" text="Загрузка чата..." />
      </div>
    );
  }

  if (error && !chat) {
    return (
      <div className="battle-chat-error">
        <HrnbAlert variant="danger" onClose={() => setError(null)}>
          {error}
        </HrnbAlert>
      </div>
    );
  }

  const chatTitle = chat?.title || (battle ? `Участники: ${battle.title}` : 'Чат участников');
  const chatDescription = chat?.chatType === ChatType.BATTLE_PARTICIPANTS
    ? 'Приватный чат для участников баттла'
    : 'Групповой чат';

  // Определяем аватар чата
  const chatAvatar = battle?.coverUrl || battle?.cover
    ? `/api/files/${battle.coverUrl || battle.cover}`
    : '/default-battle-avatar.png';

  return (
    <div className="battle-chat-tab">
      <HrnbCard className="battle-chat-card">
        <div className="battle-chat-header">
          <div className="battle-chat-header-content">
            <div className="battle-chat-avatar">
              <img
                src={chatAvatar}
                alt="Battle Avatar"
                className="battle-chat-avatar-img"
              />
            </div>
            <div className="battle-chat-header-text">
              <h3>{chatTitle}</h3>
              <p className="text-muted">{chatDescription}</p>
            </div>
          </div>
          {chat && (
            <div className="battle-chat-info">
              <span className="chat-participant-count">
                👥 {chat.participantIds?.length || chat.participants?.length || 0} участников
              </span>
              {chat.adminIds && chat.adminIds.length > 0 && (
                <span className="chat-admin-info" title="Администраторы чата">
                  👑 {chat.adminIds.length} админ
                </span>
              )}
              {isChatAdmin && (
                <HrnbButton
                  variant="primary"
                  size="sm"
                  onClick={() => setShowAddParticipantModal(true)}
                  className="add-participant-btn"
                  title="Добавить участника в чат"
                  style={{ marginLeft: '8px' }}
                >
                  <PlusIcon style={{ width: 14, height: 14, marginRight: 6 }} />
                  Добавить
                </HrnbButton>
              )}
              {chat.lastMessageAt && (
                <span className="chat-last-message">
                  Последнее сообщение: {new Date(chat.lastMessageAt).toLocaleString('ru-RU')}
                </span>
              )}
              {chat.chatType && (
                <span className="chat-type-badge">
                  {chat.chatType === ChatType.BATTLE_PARTICIPANTS ? '🎯 Участники' : chat.chatType}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Список сообщений */}
        <div className="battle-chat-messages" ref={chatContainerRef}>
          {fetchingMessages ? (
            <div className="chat-loading-messages">
              <HrnbLoader size="sm" text="Загрузка сообщений..." />
            </div>
          ) : messages.length === 0 ? (
            <div className="chat-no-messages">
              <p className="text-muted">Сообщений пока нет. Будьте первым!</p>
            </div>
          ) : (
            <div className="chat-messages-list">
              {messages.map((message, index) => {
                // Исправленная проверка isOwnMessage:
                // Сравниваем sender.userId или sender.id с currentUser.id
                const senderId = message.sender?.userId || message.sender?.id || message.senderId;
                const currentUserId = currentUser?.id || currentUser?.userId;
                const isOwnMessage = senderId === currentUserId;

                // Разделитель даты — при смене дня
                const msgDate = message.createdAt || message.sentAt;
                const prevMsgDate = index > 0
                  ? (messages[index - 1].createdAt || messages[index - 1].sentAt)
                  : null;
                const showDateSeparator = index === 0
                  || getDayKey(msgDate) !== getDayKey(prevMsgDate);

                // Группировка подряд идущих сообщений одного автора (≤ 5 мин)
                const GROUP_WINDOW_MS = 5 * 60 * 1000;
                const prevMsg = index > 0 ? messages[index - 1] : null;
                const prevSenderId = prevMsg
                  ? (prevMsg.sender?.userId || prevMsg.sender?.id || prevMsg.senderId)
                  : null;
                const prevDate = prevMsgDate ? new Date(prevMsgDate).getTime() : 0;
                const curDate = msgDate ? new Date(msgDate).getTime() : 0;
                const sameSender = prevMsg && senderId && prevSenderId && senderId === prevSenderId;
                const withinWindow = prevMsg && (curDate - prevDate) <= GROUP_WINDOW_MS;
                const breaksGroup = showDateSeparator
                  || !!message.replyToMessageId
                  || (prevMsg && !!prevMsg.replyToMessageId)
                  || message.isDeleted
                  || (prevMsg && prevMsg.isDeleted);
                const isGrouped = sameSender && withinWindow && !breaksGroup;

                const nextMsg = index < messages.length - 1 ? messages[index + 1] : null;
                const nextSenderId = nextMsg
                  ? (nextMsg.sender?.userId || nextMsg.sender?.id || nextMsg.senderId)
                  : null;
                const nextDate = nextMsg
                  ? new Date(nextMsg.createdAt || nextMsg.sentAt).getTime()
                  : 0;
                const nextWithinWindow = nextMsg && (nextDate - curDate) <= GROUP_WINDOW_MS;
                const nextBreaksGroup = nextMsg
                  && (getDayKey(nextMsg.createdAt || nextMsg.sentAt) !== getDayKey(msgDate)
                    || !!nextMsg.replyToMessageId
                    || nextMsg.isDeleted);
                const isGroupedLast = isGrouped && (!nextMsg
                  || nextSenderId !== senderId
                  || !nextWithinWindow
                  || nextBreaksGroup);

                return (
                  <React.Fragment key={message.id || index}>
                    {showDateSeparator && msgDate && (
                      <div className="chat-date-separator">
                        <span className="chat-date-separator-text">
                          {formatDateSeparator(msgDate)}
                        </span>
                      </div>
                    )}
                    <ChatMessage
                      message={message}
                      isOwn={isOwnMessage}
                      isGrouped={isGrouped}
                      isGroupedLast={isGroupedLast}
                      currentProfileId={currentUser?.profileId || currentUser?.profiles?.[0]?.id}
                      showActions={false}
                    />
                  </React.Fragment>
                );
              })}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

        {/* Форма отправки сообщения */}
        {currentUser && (
          <form className="battle-chat-form" onSubmit={handleSendMessage}>
            {/* Прикреплённые файлы */}
            {attachments.length > 0 && (
              <div className="battle-chat-attachments">
                {attachments.map((att, idx) => (
                  <div key={`${att.id}-${idx}`} className="battle-chat-attachment">
                    <span className="battle-chat-attachment-icon">
                      {getAttachmentIcon(att.type)}
                    </span>
                    <div className="battle-chat-attachment-info">
                      <span className="battle-chat-attachment-name" title={att.name}>
                        {att.name}
                      </span>
                      <span className="battle-chat-attachment-size">
                        {formatFileSize(att.size)}
                      </span>
                    </div>
                    <button
                      type="button"
                      className="battle-chat-attachment-remove"
                      onClick={() => removeAttachment(idx)}
                      title="Удалить"
                      disabled={sending}
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            )}

            {uploadError && (
              <div className="battle-chat-upload-error">{uploadError}</div>
            )}

            <div className="chat-input-wrapper">
              <button
                type="button"
                className="battle-chat-attach-btn"
                onClick={() => fileInputRef.current?.click()}
                disabled={sending || uploading}
                title="Прикрепить файл (макс. 30 MB)"
              >
                {uploading ? <HrnbLoader size="sm" /> : <UploadIcon style={{ width: 16, height: 16 }} />}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                style={{ display: 'none' }}
                onChange={handleFileSelect}
                disabled={sending || uploading}
                accept="image/*,audio/*,video/*,.pdf,.doc,.docx,.txt"
              />

              <HrnbInput
                value={messageText}
                onChange={(e) => setMessageText(e.target.value)}
                placeholder={wsConnected ? "Введите сообщение..." : "WebSocket не подключен, используется REST API..."}
                disabled={sending || uploading}
                className="chat-message-input"
              />
              <HrnbButton
                type="submit"
                variant="primary"
                disabled={sending || uploading || (!messageText.trim() && attachments.length === 0)}
                className="chat-send-btn"
              >
                {sending ? <HrnbLoader size="sm" /> : (
                  <>
                    <SendIcon style={{ width: 14, height: 14, marginRight: 6 }} />
                    Отправить
                  </>
                )}
              </HrnbButton>
            </div>
            <div className="chat-connection-status" style={{ fontSize: '0.75rem', marginTop: 8 }}>
              {wsConnected ? (
                <span style={{ color: '#28a745' }}>● WebSocket подключен</span>
              ) : (
                <span style={{ color: '#ffc107' }}>● REST API (WebSocket не подключен)</span>
              )}
            </div>
          </form>
        )}

        {!currentUser && (
          <div className="chat-auth-required">
            <HrnbAlert variant="info">
              <a href="/login">Войдите</a> или <a href="/register">зарегистрируйтесь</a>, чтобы отправлять сообщения
            </HrnbAlert>
          </div>
        )}
      </HrnbCard>

      {/* Модальное окно добавления участника */}
      {chat && (
        <BattleChatAddParticipantModal
          isOpen={showAddParticipantModal}
          onClose={() => setShowAddParticipantModal(false)}
          battleId={battleId}
          chatId={chat.id}
          existingParticipantIds={chat.participantIds || []}
          onSuccess={() => {
            // Обновляем данные чата после добавления участника
            chatsApi.getChat(chat.id).then(updatedChat => {
              setChat(updatedChat);
              setIsChatAdmin(updatedChat.adminIds?.includes(currentUser?.id) || false);
            });
          }}
        />
      )}
    </div>
  );
};

export default BattleChatTab;
